/**
 * SRES · Narrative Jobs Panel — reusable across Final Review (Stage 13)
 * and AI Report Generation (Stage 12).
 *
 * Feb 2026 · Iter76 · Same React-Query key so both mounts stay in sync
 * without any prop drilling — start a job on Stage 12, watch its
 * progress on Stage 13, resume from either.
 *
 * Backend:
 *   GET  /api/projects/{pid}/generate-all-jobs
 *   POST /api/projects/{pid}/narratives/generate-all?only_missing=…
 *   POST /api/projects/{pid}/narratives/generate-all/{job_id}/resume
 *   GET  /api/projects/{pid}/narratives/generate-all/{job_id}
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Play, ArrowClockwise, CircleNotch, Stack, ArrowsClockwise, Broadcast, WifiHigh, WifiSlash,
} from "@phosphor-icons/react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import useProjectJobsSocket from "@/hooks/useProjectJobsSocket";

// Canonical, cache-shared query key.
export const JOBS_QUERY_KEY = (pid) => ["narrative-generate-all-jobs", pid];

export default function NarrativeJobsPanel({
  projectId,
  title = "Full-project generation jobs",
  showStartButton = true,
  showOnlyMissingToggle = true,
  refetchInterval = 8_000,
  testIdPrefix = "njp",
}) {
  const qc = useQueryClient();

  // ── Iter77 · Feb 2026 · Real-time WS push ────────────────────────
  // The hook invalidates JOBS_QUERY_KEY on every job_* / paragraph_* /
  // bulk_approve_done event, so the underlying useQuery below refetches
  // instantly instead of waiting for the 8-second polling tick.
  const { status: wsStatus } = useProjectJobsSocket(projectId);
  const wsOpen = wsStatus === "open";

  // When the socket is healthy we drop polling to 30 s as a safety net.
  // If the hook has fallen back to polling-only mode we keep the fast
  // interval so behaviour is identical to the pre-Iter77 build.
  const effectiveInterval = wsOpen ? 30_000 : refetchInterval;

  const jobsQ = useQuery({
    queryKey: JOBS_QUERY_KEY(projectId),
    queryFn:  () => api.get(`/projects/${projectId}/generate-all-jobs?limit=10`)
                          .then((r) => r.data),
    enabled:  !!projectId,
    refetchInterval: effectiveInterval,
  });

  const startAllMut = useMutation({
    mutationFn: (onlyMissing) =>
      api.post(`/projects/${projectId}/narratives/generate-all?only_missing=${onlyMissing}`)
              .then((r) => r.data),
    onSuccess: (_, onlyMissing) => {
      toast.success(onlyMissing
        ? "Full-project generation started (missing chapters only)."
        : "Full-project generation started (regenerating every chapter).");
      qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY(projectId) });
    },
    onError: (e) => toast.error(
      e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message
    ),
  });

  const resumeMut = useMutation({
    mutationFn: (jobId) =>
      api.post(`/projects/${projectId}/narratives/generate-all/${jobId}/resume`)
              .then((r) => r.data),
    onSuccess: () => {
      toast.success("Interrupted job resumed.");
      qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY(projectId) });
    },
    onError: (e) => toast.error(
      e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message
    ),
  });

  const items = jobsQ.data?.items || [];
  const interruptedCount = items.filter((j) => j.status === "interrupted").length;

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden"
              data-testid={`${testIdPrefix}-panel`}>
      <header className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 flex-wrap">
        <Stack size={14} weight="fill" className="text-brand-blue" />
        <div className="font-heading font-bold text-[13px] text-slate-900">
          {title}
        </div>
        {interruptedCount > 0 && (
          <span className="ml-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700"
                  data-testid={`${testIdPrefix}-interrupted-badge`}>
            {interruptedCount} interrupted
          </span>
        )}
        <LiveIndicator status={wsStatus} testIdPrefix={testIdPrefix} />
        <div className="flex-1" />
        <Button
          size="sm" variant="ghost"
          onClick={() => qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY(projectId) })}
          data-testid={`${testIdPrefix}-refresh`}
          className="h-7 text-[11px] text-slate-500 gap-1">
          <ArrowsClockwise size={11} /> Refresh
        </Button>
        {showStartButton && (
          <>
            {showOnlyMissingToggle ? (
              <>
                <Button
                  size="sm"
                  onClick={() => startAllMut.mutate(true)}
                  disabled={startAllMut.isPending}
                  data-testid={`${testIdPrefix}-start-missing`}
                  className="h-7 text-[11px] bg-brand-blue hover:bg-brand-blue/90 text-white gap-1">
                  <Play size={11} weight="fill" />
                  Draft missing
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => startAllMut.mutate(false)}
                  disabled={startAllMut.isPending}
                  data-testid={`${testIdPrefix}-start-full`}
                  className="h-7 text-[11px] gap-1">
                  <Play size={11} weight="bold" />
                  Regenerate all
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                onClick={() => startAllMut.mutate(true)}
                disabled={startAllMut.isPending}
                data-testid={`${testIdPrefix}-start-missing`}
                className="h-7 text-[11px] bg-brand-blue hover:bg-brand-blue/90 text-white gap-1">
                <Play size={11} weight="fill" />
                {startAllMut.isPending ? "Starting…" : "Start new job"}
              </Button>
            )}
          </>
        )}
      </header>

      <div className="p-4">
        {jobsQ.isLoading && (
          <div className="text-[12px] text-slate-500 italic">Loading jobs…</div>
        )}
        {items.length === 0 && !jobsQ.isLoading && (
          <div className="text-[12px] text-slate-500 italic border border-dashed border-slate-300 rounded p-3">
            No generation jobs yet. Draft the 27 chapters using the buttons above.
          </div>
        )}
        {items.length > 0 && (
          <div className="border border-slate-200 rounded overflow-hidden text-[11.5px]">
            <table className="w-full">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="text-left  px-2 py-1.5">Job ID</th>
                  <th className="text-left  px-2 py-1.5">Status</th>
                  <th className="text-right px-2 py-1.5">Progress</th>
                  <th className="text-left  px-2 py-1.5">Started</th>
                  <th className="text-right px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((j) => (
                  <JobRow key={j.job_id}
                              job={j}
                              testIdPrefix={testIdPrefix}
                              onResume={() => resumeMut.mutate(j.job_id)}
                              resumeDisabled={resumeMut.isPending} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function JobRow({ job, testIdPrefix, onResume, resumeDisabled }) {
  const pct = job.total && job.total > 0
        ? Math.round(((job.done || 0) / job.total) * 100)
        : null;
  return (
    <tr className="border-t border-slate-100"
              data-testid={`${testIdPrefix}-job-${job.job_id}`}>
      <td className="px-2 py-1 font-mono text-[10.5px]">{job.job_id}</td>
      <td className="px-2 py-1">
        <JobStatusPill status={job.status} />
      </td>
      <td className="px-2 py-1 text-right font-mono text-[11px]">
        {job.done ?? 0}/{job.total ?? "?"}{pct != null && ` · ${pct}%`}
        {(job.blocked || 0) > 0 && ` · ${job.blocked} blocked`}
        {(job.failed || 0)  > 0 && ` · ${job.failed} failed`}
      </td>
      <td className="px-2 py-1 text-[10.5px] text-slate-500">
        {job.started_at ? new Date(job.started_at).toLocaleString() : "—"}
      </td>
      <td className="px-2 py-1 text-right">
        {job.status === "interrupted" ? (
          <Button size="sm"
                       onClick={onResume}
                       disabled={resumeDisabled}
                       data-testid={`${testIdPrefix}-resume-${job.job_id}`}
                       className="h-6 text-[10.5px] gap-1
                                       bg-amber-500 hover:bg-amber-600 text-white">
            <ArrowClockwise size={10} weight="bold" />
            Resume
          </Button>
        ) : job.status === "running" ? (
          <span className="inline-flex items-center gap-1 text-[10.5px] text-brand-blue">
            <CircleNotch size={11} weight="bold" className="animate-spin" />
            {job.current_chapter || "…"}
          </span>
        ) : (
          <span className="text-[10.5px] text-slate-400">—</span>
        )}
      </td>
    </tr>
  );
}

function JobStatusPill({ status }) {
  const tones = {
    queued:      "bg-slate-100  text-slate-700  border-slate-200",
    running:     "bg-brand-blue/10 text-brand-blue border-brand-blue/30",
    interrupted: "bg-amber-50   text-amber-700  border-amber-200",
    finished:    "bg-emerald-50 text-emerald-700 border-emerald-200",
    failed:      "bg-rose-50    text-rose-700   border-rose-200",
  };
  const t = tones[status] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-block text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${t}`}>
      {status || "unknown"}
    </span>
  );
}

function LiveIndicator({ status, testIdPrefix }) {
  // "open" → LIVE (green dot). "polling-fallback" → grey. Others → yellow spinner.
  const isLive = status === "open";
  const isPolling = status === "polling-fallback";
  const cls = isLive
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : isPolling
      ? "text-slate-500  bg-slate-50   border-slate-200"
      : "text-amber-700  bg-amber-50   border-amber-200";
  const Icon = isLive ? Broadcast : isPolling ? WifiSlash : WifiHigh;
  const label = isLive ? "Live" : isPolling ? "Polling" : "Connecting…";
  return (
    <span
      data-testid={`${testIdPrefix}-live-indicator`}
      data-live-status={status}
      title={
        isLive
          ? "Real-time WebSocket updates active"
          : isPolling
            ? "WebSocket unavailable — falling back to 8-second polling"
            : "Connecting to real-time channel…"
      }
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${cls}`}
    >
      <Icon size={10} weight={isLive ? "fill" : "bold"} />
      {label}
    </span>
  );
}
